// Server functions behind the portal pages' initial data. Each acts as the visitor: the selected
// project comes from the validated portal view and the backend sees the visitor's session
// (src/lib/RequestState.ts). Signed out, they return nothing: the `_authed` layout renders the
// sign-in page instead.

import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import type { ResponseOf } from "api-def";
import type { ConnectedApp } from "shared/AgentAccess";
import type { ApiKey } from "shared/ApiKey";
import type { Appeal } from "shared/Appeal";
import type { Asset } from "shared/Asset";
import type { SearchIndexingEstimate } from "shared/AssetSearch";
import type { ConnectorDelivery, ConnectorPublic } from "shared/Connector";
import type { Event } from "shared/Event";
import type { Folder } from "shared/Folder";
import type { HashList, HashListEntry } from "shared/HashList";
import type { Lens } from "shared/Lens";
import { type CursorPage, DEFAULT_PAGE_SIZE, type Page } from "shared/Page";
import type { PrivateAssetViewUrls } from "shared/PrivateApiSchemas";
import { ENABLE_APPEALS } from "shared/SharedConstants";
import type { SupportTicket } from "shared/SupportTicket";
import type { UploadPreset } from "shared/UploadPreset";
import {
  ALL_HASH_LISTS,
  getApiKeys,
  getAsset,
  getAssetChildren,
  getAssetsPage,
  getAssetView,
  getBillingOverview,
  getConnectedApps,
  getConnectorDeliveries,
  getConnectors,
  getDashboardStats,
  getEvents,
  getFolders,
  getHashList,
  getHashListEntries,
  getHashLists,
  getLenses,
  getOrganisationTeam,
  getOrganisationUsage,
  getSearchIndexingEstimate,
  getSupportTickets,
  getUploadPresets,
  listAppeals,
} from "src/CalmLensApi";
import {
  type AssetDashboardPersistentState,
  loadAssetsDashboardView,
  normalizeAssetDashboardFilters,
  readAssetDashboardStateCookie,
} from "src/lib/AssetDashboardUtils";
import { hasIdTokenCookie } from "src/lib/Bypass";
import { HASH_LIST_ENTRIES_PAGE_SIZE } from "src/lib/HashLists";
import { canProject } from "src/lib/ProjectPermissions";
import { fetchPortalView, fetchProjectTeam } from "src/lib/ServerUtils";
import { loadUsageDashboardView } from "src/lib/UsageDashboardUtils";

/**
 * A project page's project (from its URL, resolved by the portal layout), only once signed in.
 * The backend checks the caller can open it.
 */
const validateProjectId = (projectId: string) => projectId;
const signedIn = (projectId: string): string | undefined =>
  hasIdTokenCookie() ? projectId : undefined;

export interface PortalDashboardData {
  initialAssetPage?: Page<Asset>;
  initialFolders?: Folder[];
  initialPersistentState?: AssetDashboardPersistentState;
  initialDashboardStats?: ResponseOf<typeof getDashboardStats>;
}

/**
 * `/portal`: the first page of assets, the folders and the stat cards for the selected project, with
 * the filters, grouping and view mode saved in the `CL_assetDashboardState` cookie. Filters and
 * grouping on a metadata field the project no longer has are dropped.
 */
export const getPortalDashboardData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }): Promise<PortalDashboardData> => {
    const projectId = signedIn(data);
    if (!projectId) return {};

    let persistentState = readAssetDashboardStateCookie();
    const filters = normalizeAssetDashboardFilters(persistentState?.filters);
    const hasMetadataFilter = Boolean(
      filters.metadataFieldId && (filters.metadataValue || filters.metadataUnassigned),
    );
    const loadDashboardView = () =>
      loadAssetsDashboardView(
        projectId,
        {
          pageIndex: 0,
          pageSize: DEFAULT_PAGE_SIZE,
          ordering: [{ field: "createdAt", direction: "desc" }],
        },
        filters,
      ).catch((error) => {
        console.error("Portal dashboard failed to load initial assets or folders", error);
        throw error;
      });

    // The project's metadata fields are only needed to check a saved metadata filter or grouping.
    const needsProject = hasMetadataFilter || Boolean(persistentState?.groupByMetadataFieldId);
    const [portalView, initialView, stats] = await Promise.all([
      needsProject ? fetchPortalView({ projectId }) : undefined,
      // A metadata filter is checked against the project's fields before loading with it.
      hasMetadataFilter ? undefined : loadDashboardView(),
      // The stat cards render from this; without it they load again in the browser.
      getDashboardStats
        .submit({ params: { projectId } })
        .then((response) => response.data)
        .catch((error) => {
          console.error("Portal dashboard failed to load stats", error);
          return undefined;
        }),
    ]);

    const selectedProject = portalView?.projects.items.find((project) => project.id === projectId);
    const metadataFields = selectedProject?.settings?.metadataFields;
    if (selectedProject && !metadataFields?.some((field) => field.id === filters.metadataFieldId)) {
      filters.metadataFieldId = "";
      filters.metadataValue = "";
      filters.metadataUnassigned = false;
    }
    if (
      persistentState &&
      selectedProject &&
      !metadataFields?.some((field) => field.id === persistentState?.groupByMetadataFieldId)
    ) {
      persistentState.groupByMetadataFieldId = undefined;
    }
    if (persistentState) persistentState = { ...persistentState, filters };

    const view = initialView ?? (await loadDashboardView());
    return {
      initialAssetPage: view.assetsPage,
      initialFolders: view.folders,
      initialPersistentState: persistentState,
      initialDashboardStats: stats,
    };
  });

/** `/portal/profile`: the apps the visitor connected (AI assistants, CLI logins). */
export const getProfilePageData = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ initialConnectedApps?: ConnectedApp[] }> => {
    if (!hasIdTokenCookie()) return {};
    const response = await getConnectedApps.submit({});
    return { initialConnectedApps: response.data.connectedApps };
  },
);

/** `/portal/api-keys`: the first page of the selected project's API keys. */
export const getApiKeysPageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }): Promise<{ initialPage?: Page<ApiKey> }> => {
    const projectId = signedIn(data);
    if (!projectId) return {};
    const response = await getApiKeys.submit({
      params: { projectId },
      query: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
    });
    return { initialPage: response.data };
  });

/** `/portal/upload-presets`: the first page of the selected project's upload presets. */
export const getUploadPresetsPageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }): Promise<{ initialPage?: Page<UploadPreset>; folders?: Folder[] }> => {
    const projectId = signedIn(data);
    if (!projectId) return {};
    const [presets, folders] = await Promise.all([
      getUploadPresets.submit({
        params: { projectId },
        query: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
      }),
      getFolders.submit({ params: { projectId }, query: {} }),
    ]);
    return { initialPage: presets.data, folders: folders.data.items };
  });

/**
 * `/portal/hash-lists`: the selected project's hash lists. `projectId` names the project they belong
 * to, so the page ignores them once another project is selected.
 */
export const getHashListsPageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }): Promise<{ projectId?: string; initialLists?: HashList[] }> => {
    const projectId = signedIn(data);
    if (!projectId) return {};
    const response = await getHashLists.submit({ params: { projectId }, query: ALL_HASH_LISTS });
    return { projectId, initialLists: response.data.items };
  });

/**
 * `/portal/<project ID>/hash-lists/$hashListId`: the list and its newest entries, or a 404, with the
 * project's lists for the topbar's switcher.
 */
export const getHashListPageData = createServerFn({ method: "GET" })
  .validator((input: { projectId: string; hashListId: string }) => input)
  .handler(
    async ({
      data: { projectId, hashListId },
    }): Promise<{
      hashList?: HashList;
      entriesPage?: CursorPage<HashListEntry>;
      hashLists?: HashList[];
    }> => {
      if (!hasIdTokenCookie()) return {};
      const [hashList, entries, hashLists] = await Promise.all([
        getHashList.submit({ params: { hashListId } }).then(
          (response) => response.data,
          () => {
            throw notFound();
          },
        ),
        getHashListEntries
          .submit({ params: { hashListId }, query: { pageSize: HASH_LIST_ENTRIES_PAGE_SIZE } })
          .then(
            (response) => response.data,
            () => undefined,
          ),
        getHashLists.submit({ params: { projectId }, query: ALL_HASH_LISTS }).then(
          (response) => response.data.items,
          () => undefined,
        ),
      ]);
      return { hashList, entriesPage: entries, hashLists };
    },
  );

/** A lens as the API returns it: its transforms' options are plain JSON. */
export type PortalLens = Omit<Lens, "transforms"> & {
  transforms?: { type: string; options: JsonValue }[] | null;
};

/**
 * `/portal/lenses`: the selected project's lenses. `projectId` names the project they belong to, so
 * the page ignores them once another project is selected.
 */
export const getLensesPageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }): Promise<{ projectId?: string; initialLenses?: PortalLens[] }> => {
    const projectId = signedIn(data);
    if (!projectId) return {};
    const response = await getLenses.submit({ params: { projectId }, query: {} });
    return { projectId, initialLenses: response.data.lenses as unknown as PortalLens[] };
  });

/** Page size of the activity log on `/portal/alerts` (the first page is loaded with the document). */
const ALERTS_PAGE_SIZE = 20;

/**
 * `/portal/alerts`: the newest page of the selected project's events. `projectId` names the project
 * it belongs to, so the page ignores it once another project is selected.
 */
export const getAlertsPageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }): Promise<{ projectId?: string; initialPage?: Page<Event> }> => {
    const projectId = signedIn(data);
    if (!projectId) return {};
    const response = await getEvents.submit({
      params: { projectId },
      query: {
        pageIndex: 0,
        pageSize: ALERTS_PAGE_SIZE,
        ordering: [{ field: "createdAt", direction: "desc" }],
      },
    });
    return { projectId, initialPage: response.data };
  });

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/** A connector as the API returns it: its type-specific `config` is plain JSON. */
type ConnectorJson = Omit<ConnectorPublic, "config"> & { config: Record<string, JsonValue> };

/** Connectors and recent deliveries listed on `/portal/connectors` (deliveries across all connectors). */
const CONNECTORS_PAGE_SIZE = 100;
const CONNECTOR_DELIVERIES_PAGE_SIZE = 50;

/**
 * `/portal/connectors`: the selected project's connectors and its most recent deliveries. `projectId`
 * names the project they belong to, so the page ignores them once another project is selected.
 */
export const getConnectorsPageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(
    async ({
      data,
    }): Promise<{
      projectId?: string;
      initialConnectors?: ConnectorJson[];
      initialDeliveries?: ConnectorDelivery[];
    }> => {
      const projectId = signedIn(data);
      if (!projectId) return {};
      const [connectors, deliveries] = await Promise.all([
        getConnectors.submit({
          params: { projectId },
          query: { pageIndex: 0, pageSize: CONNECTORS_PAGE_SIZE },
        }),
        getConnectorDeliveries.submit({
          params: { projectId },
          query: { pageIndex: 0, pageSize: CONNECTOR_DELIVERIES_PAGE_SIZE },
        }),
      ]);
      return {
        projectId,
        initialConnectors: connectors.data.items as ConnectorJson[],
        initialDeliveries: deliveries.data.items,
      };
    },
  );

/** Page size of the ticket list on `/portal/support`. */
const SUPPORT_TICKETS_PAGE_SIZE = 20;

/** `/portal/support`: the first page of the visitor's support tickets (they belong to the account). */
export const getSupportPageData = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ initialPage?: Page<SupportTicket> }> => {
    if (!hasIdTokenCookie()) return {};
    const response = await getSupportTickets.submit({
      query: { pageIndex: 0, pageSize: SUPPORT_TICKETS_PAGE_SIZE },
    });
    return { initialPage: response.data };
  },
);

/**
 * `/portal/appeals` (beta): the first appeals in review for the selected project, with the project
 * they belong to.
 */
export const getAppealsPageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }): Promise<{ projectId?: string; initialPage?: Page<Appeal> }> => {
    if (!ENABLE_APPEALS) throw notFound();
    const projectId = signedIn(data);
    if (!projectId) return {};
    const response = await listAppeals.submit({
      params: { projectId },
      query: { status: "in-review", offset: 0, limit: 20 },
    });
    return { projectId, initialPage: response.data };
  });

/**
 * `/portal/settings`: the cost estimate shown before turning on audio and video search (the route
 * asks only while the project has it off). Undefined when it fails; the panel retries and reports it.
 */
export const getSettingsPageData = createServerFn({ method: "GET" })
  .validator((projectId: string) => projectId)
  .handler(
    async ({
      data: projectId,
    }): Promise<{ projectId?: string; initialEstimate?: SearchIndexingEstimate }> => {
      if (!hasIdTokenCookie()) return {};
      try {
        const response = await getSearchIndexingEstimate.submit({ params: { projectId } });
        return { projectId, initialEstimate: response.data };
      } catch (error) {
        console.error("Settings page failed to load the search indexing estimate", error);
        return { projectId };
      }
    },
  );

/**
 * `/portal/billing`: the selected organisation's usage limit, credit, spend, settings, invoices and
 * usage and cost by project, for its owners; other members see who manages billing instead.
 */
export const getBillingPageData = createServerFn({ method: "GET" })
  .validator((orgId: string) => orgId)
  .handler(async ({ data: orgId }) => {
    if (!hasIdTokenCookie()) return { initialOverview: undefined, initialUsage: undefined };
    const org = (await fetchPortalView()).organisations.find((candidate) => candidate.id === orgId);
    if (org?.role !== "owner") return { initialOverview: undefined, initialUsage: undefined };
    const [overview, usage] = await Promise.all([
      getBillingOverview.submit({ params: { orgId } }),
      getOrganisationUsage.submit({ params: { orgId } }),
    ]);
    return { initialOverview: overview.data, initialUsage: usage.data };
  });

/** `/portal/<organisation ID>/members`: the organisation's members, roles and invitations. */
export const getOrganisationPageData = createServerFn({ method: "GET" })
  .validator((orgId: string) => orgId)
  .handler(async ({ data: orgId }) => {
    if (!hasIdTokenCookie()) return { initialTeam: undefined };
    const response = await getOrganisationTeam.submit({ params: { orgId } });
    return { initialTeam: response.data };
  });

/** `/portal/usage`: the selected project's usage and billing. */
export const getUsagePageData = createServerFn({ method: "GET" })
  .validator(validateProjectId)
  .handler(async ({ data }) => {
    const projectId = signedIn(data);
    if (!projectId) return { initialDashboardData: undefined };
    const initialDashboardData = await loadUsageDashboardView(projectId).catch((error) => {
      console.error("Usage dashboard failed to load initial data", error);
      throw error;
    });
    return { initialDashboardData };
  });

export type AssetPageData =
  | { state: "signed-out" }
  | { state: "no-project" }
  | {
      state: "ready";
      asset: Asset;
      assetUrls: PrivateAssetViewUrls;
      /** The project's folders, the parent asset (null without one) and the first page of
       * children. Undefined when one fails to load: the page loads it again in the browser. */
      folders?: Folder[];
      parentAsset?: Asset | null;
      childAssetsPage?: Page<Asset> | null;
      /** The newest page of the asset's events (the activity log). */
      eventsPage?: Page<Event>;
      /** Connector deliveries of the asset's events and the project's connectors, for members who
       * may read connectors (empty for others, as in the browser). */
      connectorDeliveries?: ConnectorDelivery[];
      connectors?: ConnectorJson[];
      /** The project's hash lists, for members who may read them (empty for others): they name
       * the lists the asset matched and are the choices for adding the asset to one. */
      hashLists?: HashList[];
      /** The project's newest assets, for the topbar's asset switcher (which searches the rest). */
      siblingAssets?: Asset[];
    };

/** How many of the project's newest assets the asset page's switcher lists before a search. */
const SIBLING_ASSETS_PAGE_SIZE = 20;

/** The asset page's deliveries panel: the asset's events, their deliveries and the connectors. */
const loadAssetConnectorDeliveries = async (
  projectId: string,
  assetId: string,
): Promise<{ connectorDeliveries: ConnectorDelivery[]; connectors: ConnectorJson[] }> => {
  const team = await fetchProjectTeam(projectId);
  if (!canProject(team, "connectors:read")) return { connectorDeliveries: [], connectors: [] };
  const events = await getEvents.submit({
    params: { projectId },
    query: { pageIndex: 0, pageSize: 100, filter: `assetId==${assetId}` },
  });
  const eventIds = events.data.items.map((event) => event.id);
  if (eventIds.length === 0) return { connectorDeliveries: [], connectors: [] };
  const [deliveries, connectors] = await Promise.all([
    getConnectorDeliveries.submit({
      params: { projectId },
      query: { pageIndex: 0, pageSize: 50, filter: `eventId=in=(${eventIds.join(",")})` },
    }),
    getConnectors.submit({
      params: { projectId },
      query: { pageIndex: 0, pageSize: CONNECTORS_PAGE_SIZE },
    }),
  ]);
  return {
    connectorDeliveries: deliveries.data.items,
    connectors: connectors.data.items as ConnectorJson[],
  };
};

/** The asset page's hash lists, for members who may read them. */
const loadAssetHashLists = async (projectId: string): Promise<HashList[]> => {
  const team = await fetchProjectTeam(projectId);
  if (!canProject(team, "hash-lists:read")) return [];
  const response = await getHashLists.submit({ params: { projectId }, query: ALL_HASH_LISTS });
  return response.data.items;
};

/** Page size of the asset page's sub-asset grid (its first page loads with the document). */
const CHILD_ASSETS_PAGE_SIZE = 12;
/** Page size of the asset page's activity log. */
const ASSET_EVENTS_PAGE_SIZE = 20;

const orUndefined = <T>(promise: Promise<T>, what: string) =>
  promise.catch((error) => {
    console.error(`Asset page failed to load ${what}`, error);
    return undefined;
  });

/** `/portal/assets/$assetId`: the asset and what its page shows around it, or a 404 when the visitor
 * can't see it. */
export const getAssetPageData = createServerFn({ method: "GET" })
  .validator((input: { projectId: string; assetId: string }) => input)
  .handler(async ({ data: { projectId, assetId } }): Promise<AssetPageData> => {
    if (!hasIdTokenCookie()) return { state: "signed-out" };
    const [assetView, childAssetsPage, eventsPage, deliveries, hashLists, siblings] =
      await Promise.all([
        getAssetView.submit({ params: { assetId } }).then(
          (response) => response.data,
          () => {
            throw notFound();
          },
        ),
        orUndefined(
          getAssetChildren
            .submit({
              params: { assetId },
              query: { pageIndex: 0, pageSize: CHILD_ASSETS_PAGE_SIZE },
            })
            .then((response) => response.data),
          "sub-assets",
        ),
        orUndefined(
          getEvents
            .submit({
              params: { projectId },
              query: {
                pageIndex: 0,
                pageSize: ASSET_EVENTS_PAGE_SIZE,
                ordering: [{ field: "createdAt", direction: "desc" }],
                filter: `assetId==${assetId}`,
              },
            })
            .then((response) => response.data),
          "events",
        ),
        orUndefined(loadAssetConnectorDeliveries(projectId, assetId), "connector deliveries"),
        orUndefined(loadAssetHashLists(projectId), "hash lists"),
        orUndefined(
          getAssetsPage
            .submit({
              params: { projectId },
              query: {
                pageIndex: 0,
                pageSize: SIBLING_ASSETS_PAGE_SIZE,
                ordering: [{ field: "createdAt", direction: "desc" }],
              },
            })
            .then((response) => response.data.items),
          "the project's assets",
        ),
      ]);
    const { asset, urls: assetUrls } = assetView;
    const folderPath = asset.folderId
      ? await orUndefined(
          getFolders
            .submit({ params: { projectId }, query: { ids: [asset.folderId], resolveOnly: true } })
            .then((response) => response.data.ancestors),
          "the asset folder path",
        )
      : [];

    const parentAsset = asset.parentId
      ? await orUndefined(
          getAsset.submit({ params: { assetId: asset.parentId } }).then((r) => r.data),
          "the parent asset",
        )
      : null;
    return {
      state: "ready",
      asset,
      assetUrls,
      folders: folderPath ?? [],
      parentAsset,
      childAssetsPage,
      eventsPage,
      connectorDeliveries: deliveries?.connectorDeliveries,
      connectors: deliveries?.connectors,
      hashLists,
      siblingAssets: siblings,
    };
  });

/** The explorer has its own root listing, independent of dashboard filters. */
export const getFileExplorerData = createServerFn({ method: "GET" })
  .validator((input: { projectId: string; folderId?: string; search?: string }) => input)
  .handler(async ({ data: { folderId, search, ...input } }) => {
    const projectId = signedIn(input.projectId);
    if (!projectId) return {};
    const [folders, assets] = await Promise.all([
      getFolders.submit({
        params: { projectId },
        query: { ...(folderId ? { parentId: folderId } : {}), pageSize: 50, search },
      }),
      getAssetsPage
        .submit({
          params: { projectId },
          query: {
            pageIndex: 0,
            pageSize: 50,
            ordering: [{ field: "name", direction: "asc" }],
            ...(folderId ? { folderId } : {}),
            filter: `parentId==null;content.status!=removed${folderId ? "" : ";folderId==null"}${search?.trim() ? `;name=like=${JSON.stringify(`%${search.trim()}%`)}` : ""}`,
          },
        })
        .then((response) => response.data),
    ]);
    return {
      initialProjectId: projectId,
      initialFolderId: folderId,
      initialSearch: search ?? "",
      initialFolders: [...folders.data.ancestors, ...folders.data.items],
      initialPage: {
        ...assets,
        folders: folders.data.items,
        hasMoreFolders: folders.data.hasMore,
        foldersNextCursor: folders.data.nextCursor,
      },
    };
  });
